import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Briefcase, Plus, Loader2, Trash2, Pencil, ChevronRight, Target, Check,
  Clock, Circle, X, Search, Filter, TrendingUp, Users, AlertCircle,
  CheckCircle2, ArrowUp, ArrowDown, Calendar as CalIcon, MessageSquare,
  ArrowLeft, FileSignature, Link as LinkIcon, Save, GripVertical
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PageHeader from "../components/PageHeader";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const inputCls = "bg-[#14101c] border-white/10 text-white placeholder:text-slate-600";

const STATUS_CFG = {
  active: { label: "Actif", cls: "bg-green-500/10 text-green-400 border-green-500/20" },
  en_pause: { label: "En pause", cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  termine: { label: "Termine", cls: "bg-slate-500/10 text-slate-400 border-slate-500/20" },
};

const PHASE_STATUS = {
  todo: { label: "A faire", color: "#64748b", icon: Circle },
  in_progress: { label: "En cours", color: "#f7c935", icon: Clock },
  done: { label: "Terminee", color: "#10b981", icon: CheckCircle2 },
};

export default function AdminServicesPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [editing, setEditing] = useState(null); // service for detail view

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, cRes] = await Promise.all([
        api.get("/services"),
        api.get("/clients").catch(() => ({ data: [] })),
      ]);
      setServices(sRes.data || []);
      setClients(cRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (editing) {
    return <ServiceEditor serviceId={editing} api={api} clients={clients}
      onBack={() => { setEditing(null); load(); }} />;
  }

  const clientName = (id) => {
    const c = clients.find(c => c.user_id === id || c.client_id === id);
    return c?.name || c?.email || "—";
  };

  const filtered = services
    .filter(s => statusFilter === "all" || s.status === statusFilter)
    .filter(s => !search || (s.name + " " + (s.description || "") + " " + clientName(s.client_id)).toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "client") return clientName(a.client_id).localeCompare(clientName(b.client_id));
      if (sortBy === "progress") {
        const pa = (a.phases || []).filter(p => p.status === "done").length / Math.max(1, (a.phases || []).length);
        const pb = (b.phases || []).filter(p => p.status === "done").length / Math.max(1, (b.phases || []).length);
        return pb - pa;
      }
      return (b.created_at || "").localeCompare(a.created_at || "");
    });

  // Dashboard stats
  const total = services.length;
  const active = services.filter(s => s.status === "active").length;
  const paused = services.filter(s => s.status === "en_pause").length;
  const done = services.filter(s => s.status === "termine").length;
  const avgProgress = total ? Math.round(services.reduce((acc, s) => {
    const ph = s.phases || [];
    return acc + (ph.length ? (ph.filter(p => p.status === "done").length / ph.length) * 100 : 0);
  }, 0) / total) : 0;
  const needsAttention = services.filter(s => {
    if (s.intake_form_template_id && !s.intake_submitted) return true;
    return (s.phases || []).some(p => p.status !== "done" && p.due_date && p.due_date < new Date().toISOString().slice(0, 10));
  }).length;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="admin-services-page">
      <PageHeader
        icon={<Briefcase />}
        title="Services"
        description="Pilotez vos prestations clients : phases, liens, échanges et performance."
        testId="admin-services-header"
      />

      {/* Stats dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard icon={Briefcase} color="#f7c935" label="Services total" value={total} testid="stat-total" />
        <StatCard icon={TrendingUp} color="#10b981" label="Actifs" value={active} testid="stat-active" />
        <StatCard icon={Clock} color="#eab308" label="En pause" value={paused} testid="stat-paused" />
        <StatCard icon={CheckCircle2} color="#64748b" label="Termines" value={done} testid="stat-done" />
        <StatCard icon={AlertCircle} color="#ef4444" label="A surveiller" value={needsAttention} testid="stat-attention" />
      </div>

      {/* Avg progress bar */}
      {total > 0 && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Avancement moyen</span>
            <span className="text-2xl font-black text-[#f7c935]">{avgProgress}%</span>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#f7c935] to-[#a78bfa] transition-all" style={{ width: `${avgProgress}%` }} />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher service ou client..."
            className={`${inputCls} pl-9`} data-testid="admin-services-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className={`${inputCls} w-44`} data-testid="admin-services-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#14101c] border-white/10 text-white">
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="active">Actifs</SelectItem>
            <SelectItem value="en_pause">En pause</SelectItem>
            <SelectItem value="termine">Termines</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className={`${inputCls} w-44`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#14101c] border-white/10 text-white">
            <SelectItem value="recent">Plus recent</SelectItem>
            <SelectItem value="name">Nom</SelectItem>
            <SelectItem value="client">Client</SelectItem>
            <SelectItem value="progress">Avancement</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Services list */}
      {loading ? <Loader2 className="w-6 h-6 animate-spin text-[#f7c935] mx-auto my-10" /> : (
        filtered.length === 0 ? (
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-10 text-center">
            <Briefcase className="w-10 h-10 mx-auto text-slate-600 mb-3" />
            <p className="text-sm text-slate-500">{search || statusFilter !== "all" ? "Aucun resultat." : "Aucun service. Creez-en depuis un client."}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(s => {
              const phases = s.phases || [];
              const phDone = phases.filter(p => p.status === "done").length;
              const progress = phases.length ? Math.round((phDone / phases.length) * 100) : 0;
              const statusCfg = STATUS_CFG[s.status] || STATUS_CFG.active;
              return (
                <button key={s.service_id} onClick={() => setEditing(s.service_id)}
                  className="w-full bg-[#14101c] border border-white/5 hover:border-[#f7c935]/30 rounded-xl p-4 transition-colors text-left group"
                  data-testid={`admin-service-${s.service_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#461d8a25" }}>
                      <Briefcase className="w-5 h-5" style={{ color: "#a78bfa" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-white">{s.name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold border ${statusCfg.cls}`}>{statusCfg.label}</span>
                        {s.category && <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-slate-400">{s.category}</span>}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                        <Users className="w-2.5 h-2.5" /> {clientName(s.client_id)}
                        {s.price && <span className="text-[#f7c935]">· {s.price}</span>}
                        {phases.length > 0 && <span>· {phDone}/{phases.length} phases</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {phases.length > 0 && (
                        <div className="w-28">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[9px] text-slate-500">Avancement</span>
                            <span className="text-[10px] font-bold text-[#f7c935]">{progress}%</span>
                          </div>
                          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-[#f7c935] to-[#a78bfa]" style={{ width: `${progress}%` }} />
                          </div>
                        </div>
                      )}
                      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-[#f7c935] transition-colors" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-3 flex items-center gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}18`, color }}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-2xl font-black text-white leading-none">{value}</div>
        <div className="text-[10px] text-slate-500 mt-0.5">{label}</div>
      </div>
    </div>
  );
}


// ─── SERVICE EDITOR (phases + links + comments shortcut) ──────
function ServiceEditor({ serviceId, api, clients, onBack }) {
  const [bundle, setBundle] = useState(null);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newPhase, setNewPhase] = useState({ title: "", description: "", due_date: "", status: "todo" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/services/${serviceId}/bundle`);
      setBundle(r.data);
      setPhases((r.data.service.phases || []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [serviceId]);

  const addPhase = () => {
    if (!newPhase.title.trim()) return;
    const ph = {
      phase_id: `new_${Date.now()}`,
      title: newPhase.title,
      description: newPhase.description,
      due_date: newPhase.due_date || null,
      status: newPhase.status,
      order: phases.length,
    };
    setPhases([...phases, ph]);
    setNewPhase({ title: "", description: "", due_date: "", status: "todo" });
    setDirty(true);
  };

  const removePhase = (i) => {
    const updated = phases.filter((_, idx) => idx !== i).map((p, idx) => ({ ...p, order: idx }));
    setPhases(updated);
    setDirty(true);
  };

  const movePhase = (i, dir) => {
    const newIdx = i + dir;
    if (newIdx < 0 || newIdx >= phases.length) return;
    const updated = [...phases];
    [updated[i], updated[newIdx]] = [updated[newIdx], updated[i]];
    updated.forEach((p, idx) => p.order = idx);
    setPhases(updated);
    setDirty(true);
  };

  const updatePhase = (i, field, value) => {
    const updated = [...phases];
    updated[i] = { ...updated[i], [field]: value };
    setPhases(updated);
    setDirty(true);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.put(`/services/${serviceId}/phases`, { phases });
      setDirty(false);
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Erreur de sauvegarde"); }
    setSaving(false);
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;
  if (!bundle) return <div className="p-8 text-slate-400">Service introuvable</div>;

  const svc = bundle.service;
  const client = clients.find(c => c.user_id === svc.client_id || c.client_id === svc.client_id);
  const progress = phases.length ? Math.round((phases.filter(p => p.status === "done").length / phases.length) * 100) : 0;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid={`admin-service-editor-${serviceId}`}>
      <button onClick={onBack} className="text-xs text-slate-400 hover:text-white flex items-center gap-1 mb-4" data-testid="editor-back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour a la liste
      </button>

      {/* Header */}
      <div className="bg-gradient-to-br from-[#14101c] to-[#0b0811] border border-white/10 rounded-xl p-5 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "#461d8a25" }}>
            <Briefcase className="w-6 h-6" style={{ color: "#a78bfa" }} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-black">{svc.name}</h1>
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
              <Users className="w-3 h-3" /> {client?.name || client?.email || "—"}
              {svc.category && <span>· {svc.category}</span>}
              {svc.price && <span className="text-[#f7c935] font-bold">· {svc.price}</span>}
            </div>
          </div>
        </div>
        {phases.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5 text-xs">
              <span className="text-slate-500 uppercase tracking-wider font-bold">Avancement</span>
              <span className="font-bold text-[#f7c935]">{progress}%</span>
            </div>
            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#f7c935] to-[#a78bfa]" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Phases editor */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Target className="w-4 h-4 text-[#f7c935]" /> Phases du parcours ({phases.length})
          </h3>
          {dirty && (
            <Button onClick={saveAll} disabled={saving} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="save-phases-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Enregistrer
            </Button>
          )}
        </div>

        {phases.length === 0 && <p className="text-xs text-slate-500 italic py-3">Aucune phase. Ajoutez la premiere etape du parcours ci-dessous.</p>}

        <div className="space-y-2 mb-4">
          {phases.map((p, i) => {
            const cfg = PHASE_STATUS[p.status] || PHASE_STATUS.todo;
            const Icon = cfg.icon;
            return (
              <div key={p.phase_id || i} className="bg-[#0b0811] border border-white/5 rounded-lg p-3" data-testid={`phase-editor-${i}`}>
                <div className="flex items-start gap-2">
                  <div className="flex flex-col gap-0.5 mt-1">
                    <button onClick={() => movePhase(i, -1)} disabled={i === 0} className="p-0.5 rounded hover:bg-white/5 disabled:opacity-30" data-testid={`phase-up-${i}`}>
                      <ArrowUp className="w-3 h-3 text-slate-500" />
                    </button>
                    <button onClick={() => movePhase(i, 1)} disabled={i === phases.length - 1} className="p-0.5 rounded hover:bg-white/5 disabled:opacity-30" data-testid={`phase-down-${i}`}>
                      <ArrowDown className="w-3 h-3 text-slate-500" />
                    </button>
                  </div>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: cfg.color + "20", border: `1px solid ${cfg.color}40` }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input value={p.title} onChange={e => updatePhase(i, "title", e.target.value)} placeholder="Titre de la phase"
                      className={`${inputCls} text-sm font-semibold h-8`} data-testid={`phase-title-${i}`} />
                    <Textarea value={p.description || ""} onChange={e => updatePhase(i, "description", e.target.value)} placeholder="Description (optionnel)"
                      rows={2} className={`${inputCls} text-xs`} />
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="date" value={p.due_date || ""} onChange={e => updatePhase(i, "due_date", e.target.value)}
                        className={`${inputCls} text-xs h-8`} data-testid={`phase-due-${i}`} />
                      <Select value={p.status} onValueChange={v => updatePhase(i, "status", v)}>
                        <SelectTrigger className={`${inputCls} h-8 text-xs`} data-testid={`phase-status-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#14101c] border-white/10 text-white">
                          {Object.entries(PHASE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <button onClick={() => removePhase(i)} className="p-1.5 rounded hover:bg-red-500/10 flex-shrink-0" data-testid={`phase-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add phase */}
        <div className="bg-[#0b0811] border border-dashed border-white/10 rounded-lg p-3">
          <div className="text-[10px] uppercase text-slate-500 font-bold mb-2 tracking-wider">Nouvelle phase</div>
          <div className="space-y-2">
            <Input value={newPhase.title} onChange={e => setNewPhase({ ...newPhase, title: e.target.value })}
              placeholder="Titre (ex: Diagnostic initial)" className={`${inputCls} h-8 text-sm`} data-testid="new-phase-title" />
            <Textarea value={newPhase.description} onChange={e => setNewPhase({ ...newPhase, description: e.target.value })}
              placeholder="Description (optionnel)" rows={2} className={`${inputCls} text-xs`} />
            <div className="grid grid-cols-3 gap-2">
              <Input type="date" value={newPhase.due_date} onChange={e => setNewPhase({ ...newPhase, due_date: e.target.value })}
                className={`${inputCls} text-xs h-8`} />
              <Select value={newPhase.status} onValueChange={v => setNewPhase({ ...newPhase, status: v })}>
                <SelectTrigger className={`${inputCls} h-8 text-xs`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white">
                  {Object.entries(PHASE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={addPhase} disabled={!newPhase.title.trim()} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold h-8" data-testid="add-phase-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Links summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <LinkCard icon={FileSignature} label="Intake form" value={svc.intake_form_template_id ? "Requis" : "Aucun"} color="#f7c935" />
        <LinkCard icon={LinkIcon} label="Accompagnement lie" value={bundle.accompaniment?.title || "Aucun"} color="#a78bfa" />
        <LinkCard icon={MessageSquare} label="Echanges client" value={`Suivre dans /services/${serviceId}`} color="#3b82f6" />
      </div>
    </div>
  );
}

function LinkCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#14101c] border border-white/5 rounded-lg p-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}18`, color }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
        <div className="text-sm font-semibold text-white truncate">{value}</div>
      </div>
    </div>
  );
}
